'use client';

import { createClient } from '@/lib/supabase/client';

export interface UserProfile {
  id: string;
  email: string;
  name: string;
  role: 'client' | 'admin';
  accountNumber?: string;
  accountType?: string;
  leverage?: string;
  accountStatus?: string;
  joinDate?: string;
  kyc?: string;
  kycDocuments?: any[];
  isSuspended?: boolean;
  profilePicture?: string;
  phone?: string;
  isManualClient?: boolean;
}

/** Canonical end-user role is "client". Legacy "trader" is still accepted. */
export function normalizeRole(role: unknown): 'client' | 'admin' {
  const value = String(role ?? '').toLowerCase().trim();
  if (value === 'admin' || value === 'super_admin' || value === 'superadmin') return 'admin';
  // client (canonical), trader (legacy), or any other non-admin role
  return 'client';
}

export function isAdminRole(role: unknown): boolean {
  return normalizeRole(role) === 'admin';
}

export function isClientRole(role: unknown): boolean {
  const value = String(role ?? '').toLowerCase().trim();
  return value === 'client' || value === 'trader' || normalizeRole(value) === 'client';
}

export interface Transaction {
  id: string;
  userId: string;
  type: 'deposit' | 'withdrawal' | 'profit' | 'deduction' | 'bonus' | 'referral_bonus' | 'pending_payment';
  amount: number;
  method: string;
  status: 'Pending' | 'Approved' | 'Rejected';
  workflowStatus?: 'Pending' | 'Under Review' | 'Approved' | 'Rejected' | 'Completed';
  adminNotes?: string;
  reviewedAt?: string;
  date: string;
  reference: string;
  notes: string;
}

export interface PaymentMethod {
  id: string;
  type: 'crypto' | 'bank';
  name?: string;
  network?: string;
  address?: string;
  qrNote?: string;
  bankName?: string;
  accountName?: string;
  accountNumber?: string;
  routingNumber?: string;
  swiftCode?: string;
  iban?: string;
  currency?: string;
  isActive: boolean;
  forManualClientsOnly?: boolean;
}

export interface ClientWithdrawalAccount {
  id: string;
  userId: string;
  type: 'crypto' | 'bank';
  label?: string;
  network?: string;
  address?: string;
  bankName?: string;
  accountName?: string;
  accountNumber?: string;
  routingNumber?: string;
  swiftCode?: string;
  iban?: string;
  currency?: string;
  createdAt: string;
}

function mapProfile(row: any): UserProfile {
  return {
    id: row.id,
    email: row.email,
    // Live DB uses full_name; migrations/local may use name
    name: row.full_name ?? row.name ?? '',
    role: normalizeRole(row.role),
    accountNumber: row.account_number,
    accountType: row.account_type,
    leverage: row.leverage,
    accountStatus: row.account_status,
    joinDate: row.join_date,
    kyc: row.kyc,
    kycDocuments: row.kyc_documents ?? [],
    isSuspended: row.is_suspended,
    profilePicture: row.profile_picture,
    phone: row.phone,
    isManualClient: row.is_manual_client ?? false,
  };
}

function mapTransaction(row: any): Transaction {
  return {
    id: row.id,
    userId: row.user_id,
    type: row.type,
    amount: Number(row.amount),
    method: row.method,
    status: row.status,
    workflowStatus: row.workflow_status,
    adminNotes: row.admin_notes,
    reviewedAt: row.reviewed_at,
    date: row.date,
    reference: row.reference,
    notes: row.notes ?? '',
  };
}

function mapPaymentMethod(row: any): PaymentMethod {
  return {
    id: row.id,
    type: row.type,
    name: row.name,
    network: row.network,
    address: row.address,
    qrNote: row.qr_note,
    bankName: row.bank_name,
    accountName: row.account_name,
    accountNumber: row.account_number,
    routingNumber: row.routing_number,
    swiftCode: row.swift_code,
    iban: row.iban,
    currency: row.currency,
    isActive: row.is_active,
    forManualClientsOnly: row.for_manual_clients_only ?? false,
  };
}

function mapWithdrawalAccount(row: any): ClientWithdrawalAccount {
  return {
    id: row.id,
    userId: row.user_id,
    type: row.type,
    label: row.label,
    network: row.network,
    address: row.address,
    bankName: row.bank_name,
    accountName: row.account_name,
    accountNumber: row.account_number,
    routingNumber: row.routing_number,
    swiftCode: row.swift_code,
    iban: row.iban,
    currency: row.currency,
    createdAt: row.created_at,
  };
}

// ─── USER PROFILES ────────────────────────────────────────────────────────────

export async function getMyProfile(): Promise<UserProfile | null> {
  const supabase = createClient();
  // Prefer local session (avoids extra auth round-trip races right after login)
  const { data: { session } } = await supabase.auth.getSession();
  const user = session?.user ?? (await supabase.auth.getUser()).data.user;
  if (!user) return null;
  const { data, error } = await supabase
    .from('user_profiles')
    .select('*')
    .eq('id', user.id)
    .single();
  if (error || !data) return null;
  return mapProfile(data);
}

/** Retrieve the current session access token for use in admin API calls. */
async function getAccessToken(): Promise<string | null> {
  try {
    const supabase = createClient();
    const { data: { session } } = await supabase.auth.getSession();
    return session?.access_token ?? null;
  } catch {
    return null;
  }
}

/** Build fetch headers that include the Authorization token when available. */
async function adminFetchHeaders(): Promise<HeadersInit> {
  const token = await getAccessToken();
  const headers: HeadersInit = {};
  if (token) headers['Authorization'] = `Bearer ${token}`;
  return headers;
}

export async function getAllClients(): Promise<UserProfile[]> {
  try {
    const headers = await adminFetchHeaders();
    const res = await fetch('/api/admin/get-clients?includeManual=false', { cache: 'no-store', headers });
    if (res.ok) {
      const json = await res.json();
      const clients = (json.clients ?? []).map(mapProfile).filter((p: UserProfile) => isClientRole(p.role));
      if (clients.length > 0) return clients;
    }
    // Fallback: direct Supabase query (works when cookie session is valid)
    const supabase = createClient();
    const { data, error } = await supabase
      .from('user_profiles')
      .select('*')
      .in('role', ['client', 'trader'])
      .eq('is_manual_client', false)
      .order('created_at', { ascending: false });
    if (error || !data) return [];
    return data.map(mapProfile).filter((p: UserProfile) => isClientRole(p.role));
  } catch {
    return [];
  }
}

/** Super admin only — returns ALL clients including manually registered ones */
export async function superAdminGetAllClients(): Promise<UserProfile[]> {
  try {
    // Primary: direct Supabase query — always returns all clients including
    // manually registered ones, regardless of role string casing.
    const supabase = createClient();
    const { data, error } = await supabase
      .from('user_profiles')
      .select('*')
      .in('role', ['client', 'trader'])
      .order('created_at', { ascending: false });
    if (!error && data && data.length > 0) {
      return data.map(mapProfile).filter((p: UserProfile) => isClientRole(p.role));
    }
    // Fallback: API route (service role, bypasses RLS)
    const headers = await adminFetchHeaders();
    const res = await fetch('/api/admin/get-clients?includeManual=true', { cache: 'no-store', headers });
    if (res.ok) {
      const json = await res.json();
      return (json.clients ?? []).map(mapProfile).filter((p: UserProfile) => isClientRole(p.role));
    }
    return [];
  } catch {
    return [];
  }
}

export async function updateProfile(userId: string, updates: Partial<{
  name: string;
  phone: string;
  profilePicture: string;
  accountType: string;
  leverage: string;
  kyc: string;
  kycDocuments: any[];
  isSuspended: boolean;
  accountStatus: string;
  accountNumber: string;
}>): Promise<void> {
  const supabase = createClient();
  const dbUpdates: any = {};
  // Live DB column is full_name
  if (updates.name !== undefined) dbUpdates.full_name = updates.name;
  if (updates.phone !== undefined) dbUpdates.phone = updates.phone;
  if (updates.profilePicture !== undefined) dbUpdates.profile_picture = updates.profilePicture;
  if (updates.accountType !== undefined) dbUpdates.account_type = updates.accountType;
  if (updates.leverage !== undefined) dbUpdates.leverage = updates.leverage;
  if (updates.kyc !== undefined) dbUpdates.kyc = updates.kyc;
  if (updates.kycDocuments !== undefined) dbUpdates.kyc_documents = updates.kycDocuments;
  if (updates.isSuspended !== undefined) dbUpdates.is_suspended = updates.isSuspended;
  if (updates.accountStatus !== undefined) dbUpdates.account_status = updates.accountStatus;
  if (updates.accountNumber !== undefined) dbUpdates.account_number = updates.accountNumber;
  await supabase.from('user_profiles').update(dbUpdates).eq('id', userId);
}

export async function createClientProfile(data: {
  email: string;
  password: string;
  name: string;
  accountNumber: string;
  accountType: string;
  leverage: string;
  kyc: string;
  accountStatus: string;
}): Promise<{ error?: string }> {
  const headers = await adminFetchHeaders();
  const res = await fetch('/api/admin/create-user', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...headers },
    body: JSON.stringify({
      email: data.email,
      password: data.password,
      name: data.name,
      accountNumber: data.accountNumber,
      accountType: data.accountType,
      leverage: data.leverage,
      kyc: data.kyc,
      accountStatus: data.accountStatus,
      isManualClient: false,
    }),
  });
  const json = await res.json();
  if (!res.ok) return { error: json.error ?? 'Failed to create client' };
  return {};
}

export async function createManualClientProfile(data: {
  email: string;
  password: string;
  name: string;
  accountNumber: string;
  accountType: string;
  leverage: string;
  kyc: string;
  accountStatus: string;
  phone?: string;
}): Promise<{ error?: string; credentials?: { email: string; password: string } }> {
  const headers = await adminFetchHeaders();
  const res = await fetch('/api/admin/create-user', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...headers },
    body: JSON.stringify({
      email: data.email,
      password: data.password,
      name: data.name,
      accountNumber: data.accountNumber,
      accountType: data.accountType,
      leverage: data.leverage,
      kyc: data.kyc,
      accountStatus: data.accountStatus,
      phone: data.phone,
      isManualClient: true,
    }),
  });
  const json = await res.json();
  if (!res.ok) return { error: json.error ?? 'Failed to create client' };
  return { credentials: { email: data.email, password: data.password } };
}

export async function deleteClientProfile(userId: string): Promise<void> {
  const supabase = createClient();
  await supabase.from('user_profiles').delete().eq('id', userId);
}

// ─── TRANSACTIONS ─────────────────────────────────────────────────────────────

export async function getMyTransactions(): Promise<Transaction[]> {
  try {
    const res = await fetch('/api/client/transactions', { cache: 'no-store' });
    if (!res.ok) {
      // Fallback to direct Supabase client if API route fails
      const supabase = createClient();
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return [];
      const { data, error } = await supabase
        .from('transactions')
        .select('*')
        .eq('user_id', user.id)
        .order('date', { ascending: false });
      if (error || !data) return [];
      return data.map(mapTransaction);
    }
    const json = await res.json();
    return (json.transactions ?? []).map(mapTransaction);
  } catch {
    // Fallback to direct Supabase client
    const supabase = createClient();
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return [];
    const { data, error } = await supabase
      .from('transactions')
      .select('*')
      .eq('user_id', user.id)
      .order('date', { ascending: false });
    if (error || !data) return [];
    return data.map(mapTransaction);
  }
}

export async function getAllTransactions(): Promise<Transaction[]> {
  try {
    const headers = await adminFetchHeaders();
    const res = await fetch('/api/admin/transactions', { cache: 'no-store', headers });
    if (res.ok) {
      const json = await res.json();
      const txns = (json.transactions ?? []).map(mapTransaction);
      if (txns.length > 0) return txns;
    }
    // Fallback: direct Supabase query
    const supabase = createClient();
    const { data, error } = await supabase
      .from('transactions')
      .select('*')
      .order('date', { ascending: false });
    if (error || !data) return [];
    return data.map(mapTransaction);
  } catch {
    return [];
  }
}

export async function getTransactionsByUser(userId: string): Promise<Transaction[]> {
  try {
    const headers = await adminFetchHeaders();
    const res = await fetch(`/api/admin/transactions?userId=${encodeURIComponent(userId)}`, { cache: 'no-store', headers });
    if (!res.ok) {
      // Fallback to direct client
      const supabase = createClient();
      const { data, error } = await supabase
        .from('transactions')
        .select('*')
        .eq('user_id', userId)
        .order('date', { ascending: false });
      if (error || !data) return [];
      return data.map(mapTransaction);
    }
    const json = await res.json();
    return (json.transactions ?? []).map(mapTransaction);
  } catch {
    const supabase = createClient();
    const { data, error } = await supabase
      .from('transactions')
      .select('*')
      .eq('user_id', userId)
      .order('date', { ascending: false });
    if (error || !data) return [];
    return data.map(mapTransaction);
  }
}

export async function createTransaction(txn: Omit<Transaction, 'id'>): Promise<Transaction | null> {
  const supabase = createClient();
  const { data, error } = await supabase
    .from('transactions')
    .insert({
      user_id: txn.userId,
      type: txn.type,
      amount: txn.amount,
      method: txn.method,
      status: txn.status,
      workflow_status: txn.workflowStatus ?? 'Pending',
      date: txn.date,
      reference: txn.reference,
      notes: txn.notes,
    })
    .select()
    .single();
  if (error || !data) return null;
  return mapTransaction(data);
}

export async function updateTransactionWorkflow(
  txnId: string,
  workflowStatus: Transaction['workflowStatus'],
  adminNotes?: string
): Promise<void> {
  try {
    const headers = await adminFetchHeaders();
    const res = await fetch('/api/admin/transactions', {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', ...headers },
      body: JSON.stringify({ txnId, workflowStatus, adminNotes }),
    });
    if (res.ok) return;
    // Fallback: direct Supabase update
    const supabase = createClient();
    const statusMap: Record<string, string> = {
      Approved: 'Approved', Completed: 'Approved', Rejected: 'Rejected',
      Pending: 'Pending', 'Under Review': 'Pending',
    };
    await supabase.from('transactions').update({
      workflow_status: workflowStatus,
      status: statusMap[workflowStatus ?? 'Pending'] ?? 'Pending',
      admin_notes: adminNotes ?? '',
      reviewed_at: new Date().toISOString(),
    }).eq('id', txnId);
  } catch {
    // silently fail
  }
}

export async function adminCreateTransaction(txn: Omit<Transaction, 'id'>): Promise<Transaction | null> {
  try {
    const headers = await adminFetchHeaders();
    const res = await fetch('/api/admin/transactions', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...headers },
      body: JSON.stringify({
        userId: txn.userId,
        type: txn.type,
        amount: txn.amount,
        method: txn.method,
        status: txn.status,
        workflowStatus: txn.workflowStatus ?? 'Approved',
        adminNotes: txn.adminNotes ?? '',
        date: txn.date,
        reference: txn.reference,
        notes: txn.notes,
      }),
    });
    if (!res.ok) return null;
    const json = await res.json();
    return json.transaction ? mapTransaction(json.transaction) : null;
  } catch {
    return null;
  }
}

export async function deleteTransaction(txnId: string): Promise<void> {
  try {
    // Use admin API route (service role) to bypass RLS for delete
    const headers = await adminFetchHeaders();
    await fetch(`/api/admin/transactions?id=${encodeURIComponent(txnId)}`, { method: 'DELETE', headers });
  } catch {
    // silently fail
  }
}

export async function updateTransaction(
  txnId: string,
  updates: Partial<{
    type: Transaction['type'];
    amount: number;
    method: string;
    status: Transaction['status'];
    reference: string;
    notes: string;
    date: string;
  }>
): Promise<{ error?: string }> {
  try {
    // Try admin API route first (uses service role, bypasses RLS)
    const headers = await adminFetchHeaders();
    const res = await fetch('/api/admin/transactions', {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', ...headers },
      body: JSON.stringify({ txnId, ...updates }),
    });
    if (res.ok) return {};
    // Fallback: direct Supabase client
    const supabase = createClient();
    const dbUpdates: any = {};
    if (updates.type !== undefined) dbUpdates.type = updates.type;
    if (updates.amount !== undefined) dbUpdates.amount = updates.amount;
    if (updates.method !== undefined) dbUpdates.method = updates.method;
    if (updates.status !== undefined) {
      dbUpdates.status = updates.status;
      dbUpdates.workflow_status = updates.status === 'Approved' ? 'Approved' : updates.status === 'Rejected' ? 'Rejected' : 'Pending';
    }
    if (updates.reference !== undefined) dbUpdates.reference = updates.reference;
    if (updates.notes !== undefined) dbUpdates.notes = updates.notes;
    if (updates.date !== undefined) dbUpdates.date = updates.date;
    const { error } = await supabase.from('transactions').update(dbUpdates).eq('id', txnId);
    if (error) return { error: error.message };
    return {};
  } catch (e: any) {
    return { error: e?.message ?? 'Update failed' };
  }
}

// ─── BALANCE ──────────────────────────────────────────────────────────────────
// The balance lives on trader_accounts and is maintained by the database from
// three ledgers: `transactions` (funding approved here), `trades` (realised P&L
// from the webtrader) and `balance_transactions` (manual manager adjustments).
//
// This used to be summed in the browser from `transactions` alone, which meant
// the dashboard and the webtrader showed different numbers for the same client —
// trading profits never appeared here, and approved deposits never appeared
// there. Read the stored value instead so both apps agree.

export interface TradingAccount {
  id: string;
  userId: string;
  accountNumber: string;
  balance: number;
  equity: number;
  usedMargin: number;
  freeMargin: number;
  marginLevel: number;
  leverage: number;
  currency: string;
}

function mapTradingAccount(row: any): TradingAccount {
  return {
    id: row.id,
    userId: row.user_id,
    accountNumber: row.account_number,
    balance: Number(row.balance ?? 0),
    equity: Number(row.equity ?? 0),
    usedMargin: Number(row.used_margin ?? 0),
    freeMargin: Number(row.free_margin ?? 0),
    marginLevel: Number(row.margin_level ?? 0),
    leverage: Number(row.leverage ?? 400),
    currency: row.currency ?? 'USD',
  };
}

export async function getTradingAccount(userId?: string): Promise<TradingAccount | null> {
  const supabase = createClient();

  let targetId = userId;
  if (!targetId) {
    const {
      data: { session },
    } = await supabase.auth.getSession();
    const user = session?.user ?? (await supabase.auth.getUser()).data.user;
    if (!user) return null;
    targetId = user.id;
  }

  const { data, error } = await supabase
    .from('trader_accounts')
    .select('*')
    .eq('user_id', targetId)
    .eq('is_active', true)
    .order('created_at', { ascending: true })
    .limit(1)
    .maybeSingle();

  if (error || !data) return null;
  return mapTradingAccount(data);
}

/** Current balance for a user, or 0 when no trading account exists yet. */
export async function getBalance(userId?: string): Promise<number> {
  const supabase = createClient();
  let targetId = userId;
  if (!targetId) {
    const { data: { session } } = await supabase.auth.getSession();
    const user = session?.user ?? (await supabase.auth.getUser()).data.user;
    if (!user) return 0;
    targetId = user.id;
  }

  // Primary: use the admin balances API (service role) — bypasses RLS completely
  // so manual clients and clients with stale trader_accounts both get correct values.
  try {
    const token = await getAccessToken();
    const headers: HeadersInit = {};
    if (token) headers['Authorization'] = `Bearer ${token}`;
    const res = await fetch(
      `/api/admin/balances?userIds=${encodeURIComponent(targetId)}`,
      { cache: 'no-store', headers }
    );
    if (res.ok) {
      const json = await res.json();
      const bal = json.balances?.[targetId];
      if (bal !== undefined) return Number(bal);
    }
  } catch {
    // fall through to direct Supabase fallback
  }

  // Fallback: direct Supabase query (may be limited by RLS but better than nothing)
  const { data: txns } = await supabase
    .from('transactions')
    .select('type, amount')
    .eq('user_id', targetId)
    .eq('status', 'Approved');

  if (txns && txns.length > 0) {
    return txns.reduce((sum: number, t: any) => {
      const amount = Math.abs(Number(t.amount ?? 0));
      const type = String(t.type ?? '');
      if (['deposit', 'profit', 'bonus', 'referral_bonus'].includes(type)) return sum + amount;
      if (['withdrawal', 'deduction'].includes(type)) return sum - amount;
      return sum;
    }, 0);
  }

  return 0;
}

/**
 * Balances for many users in one query — for the admin client list.
 * Always computes from approved transactions (service role, bypasses RLS).
 */
export async function getBalancesByUser(userIds: string[]): Promise<Record<string, number>> {
  if (!userIds.length) return {};

  // Primary: use admin API route (service role, bypasses RLS) — always transaction-based
  try {
    const token = await getAccessToken();
    const headers: HeadersInit = {};
    if (token) headers['Authorization'] = `Bearer ${token}`;

    const res = await fetch(
      `/api/admin/balances?userIds=${encodeURIComponent(userIds.join(','))}`,
      { cache: 'no-store', headers }
    );
    if (res.ok) {
      const json = await res.json();
      const apiResult: Record<string, number> = json.balances ?? {};
      // Ensure every requested id has an entry
      userIds.forEach((id) => {
        if (!(id in apiResult)) apiResult[id] = 0;
      });
      return apiResult;
    }
  } catch {
    // fall through to direct Supabase fallback
  }

  // Fallback: direct Supabase client — compute from transactions
  const supabase = createClient();
  const result: Record<string, number> = {};
  userIds.forEach((id) => { result[id] = 0; });

  const { data: txnData } = await supabase
    .from('transactions')
    .select('user_id, type, amount')
    .in('user_id', userIds)
    .eq('status', 'Approved');

  (txnData ?? []).forEach((t: any) => {
    const uid = t.user_id;
    if (!(uid in result)) result[uid] = 0;
    const amount = Math.abs(Number(t.amount ?? 0));
    const type = String(t.type ?? '');
    if (['deposit', 'profit', 'bonus', 'referral_bonus'].includes(type)) {
      result[uid] += amount;
    } else if (['withdrawal', 'deduction'].includes(type)) {
      result[uid] -= amount;
    }
  });

  return result;
}

// ─── PAYMENT METHODS ──────────────────────────────────────────────────────────

export async function getPaymentMethods(): Promise<PaymentMethod[]> {
  try {
    const headers = await adminFetchHeaders();
    const res = await fetch('/api/admin/payment-methods', { cache: 'no-store', headers });
    if (!res.ok) {
      // Fallback to direct Supabase client for client-side reads (RLS allows SELECT)
      const supabase = createClient();
      const { data, error } = await supabase
        .from('payment_methods')
        .select('*')
        .order('created_at', { ascending: true });
      if (error || !data) return [];
      return data.map(mapPaymentMethod);
    }
    const json = await res.json();
    return (json.paymentMethods ?? []).map(mapPaymentMethod);
  } catch {
    return [];
  }
}

export async function createPaymentMethod(pm: Omit<PaymentMethod, 'id'>): Promise<PaymentMethod | null> {
  try {
    const headers = await adminFetchHeaders();
    const res = await fetch('/api/admin/payment-methods', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...headers },
      body: JSON.stringify(pm),
    });
    if (!res.ok) return null;
    const json = await res.json();
    return json.paymentMethod ? mapPaymentMethod(json.paymentMethod) : null;
  } catch {
    return null;
  }
}

export async function updatePaymentMethod(id: string, updates: Partial<PaymentMethod>): Promise<void> {
  try {
    const headers = await adminFetchHeaders();
    await fetch('/api/admin/payment-methods', {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', ...headers },
      body: JSON.stringify({ id, ...updates }),
    });
  } catch {
    // silently fail
  }
}

export async function deletePaymentMethod(id: string): Promise<void> {
  try {
    const headers = await adminFetchHeaders();
    await fetch(`/api/admin/payment-methods?id=${id}`, { method: 'DELETE', headers });
  } catch {
    // silently fail
  }
}

// ─── CLIENT WITHDRAWAL ACCOUNTS ───────────────────────────────────────────────

export async function getClientWithdrawalAccounts(userId: string): Promise<ClientWithdrawalAccount[]> {
  const supabase = createClient();
  const { data, error } = await supabase
    .from('client_withdrawal_accounts')
    .select('*')
    .eq('user_id', userId)
    .order('created_at', { ascending: true });
  if (error || !data) return [];
  return data.map(mapWithdrawalAccount);
}

export async function createWithdrawalAccount(account: Omit<ClientWithdrawalAccount, 'id' | 'createdAt'>): Promise<ClientWithdrawalAccount | null> {
  const supabase = createClient();
  const { data, error } = await supabase
    .from('client_withdrawal_accounts')
    .insert({
      user_id: account.userId,
      type: account.type,
      label: account.label,
      network: account.network,
      address: account.address,
      bank_name: account.bankName,
      account_name: account.accountName,
      account_number: account.accountNumber,
      routing_number: account.routingNumber,
      swift_code: account.swiftCode,
      iban: account.iban,
      currency: account.currency,
    })
    .select()
    .single();
  if (error || !data) return null;
  return mapWithdrawalAccount(data);
}

export async function deleteWithdrawalAccount(id: string): Promise<void> {
  const supabase = createClient();
  await supabase.from('client_withdrawal_accounts').delete().eq('id', id);
}

// ─── NOTIFICATIONS ────────────────────────────────────────────────────────────

export async function getMyNotifications(userId: string) {
  const supabase = createClient();
  const { data, error } = await supabase
    .from('notifications')
    .select('*')
    .eq('user_id', userId)
    .order('created_at', { ascending: false })
    .limit(50);
  if (error || !data) return [];
  return data;
}

export async function markNotificationRead(id: string): Promise<void> {
  const supabase = createClient();
  await supabase.from('notifications').update({ is_read: true }).eq('id', id);
}

export async function markAllNotificationsRead(userId: string): Promise<void> {
  const supabase = createClient();
  await supabase.from('notifications').update({ is_read: true }).eq('user_id', userId);
}

export async function createNotification(userId: string, type: string, title: string, message: string): Promise<void> {
  const supabase = createClient();
  await supabase.from('notifications').insert({ user_id: userId, type, title, message });
}

// ─── HELPERS ─────────────────────────────────────────────────────────────────

export function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 }).format(amount);
}

export function formatDate(dateStr: string): string {
  const d = new Date(dateStr);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  const hours = String(d.getHours()).padStart(2, '0');
  const mins = String(d.getMinutes()).padStart(2, '0');
  return `${year}-${month}-${day} ${hours}:${mins}`;
}

export function getTransactionTypeLabel(type: Transaction['type']): string {
  const labels: Record<Transaction['type'], string> = {
    deposit: 'Deposit',
    withdrawal: 'Withdrawal',
    profit: 'Profit',
    deduction: 'Deduction',
    bonus: 'Bonus',
    referral_bonus: 'Referral Bonus',
    pending_payment: 'Pending Payment',
  };
  return labels[type] ?? type;
}

export function getTransactionTypeColor(type: Transaction['type']): string {
  const colors: Record<Transaction['type'], string> = {
    deposit: '#10B981',
    withdrawal: '#EF4444',
    profit: '#06B6D4',
    deduction: '#F97316',
    bonus: '#8B5CF6',
    referral_bonus: '#EC4899',
    pending_payment: '#F59E0B',
  };
  return colors[type] ?? '#6B7280';
}

export function generateReference(prefix: string): string {
  return `${prefix}-${Date.now().toString().slice(-6)}`;
}
